// Scoped, catalog-backed infinite-resource runtime.
//
// A paginated catalog entry owns its resource id, full QueryScope key, initial
// cursor, and server adapter. This module owns only TanStack page accumulation,
// cancellation, and the stable rows/loading projection. Keeping those roles
// separate is what prevents a page from inventing an unscoped query key or
// smuggling a cursor into a cache key.
import {skipToken, useInfiniteQuery} from '@tanstack/react-query';
import type {InfiniteData, QueryKey, UseInfiniteQueryOptions} from '@tanstack/react-query';
import {useCallback, useMemo, useRef} from 'react';

import {useAuth} from '../../contexts/AuthContext.jsx';
import {sb} from '../../lib/supabase.js';
import {getPaginatedResource, scopeOrNull, UNRESOLVED_RESOURCE_KEY} from './routeDataCatalog.js';
import type {
  PaginatedPage as CatalogPaginatedPage,
  PaginatedRouteResource,
  ResourceParams,
} from './routeDataCatalog.js';
import type {SupabaseClient} from '../../lib/supabaseTypes.js';

// The catalog owns the exact page shape. Re-export it here so a future surface
// can type the first-page count/metadata it receives from this runtime without
// importing an implementation detail from the catalog module.
export type PaginatedPage<Row, PageParam, Meta extends object = Record<never, never>> = CatalogPaginatedPage<
  Row,
  PageParam,
  Meta
>;

/** The pure page adapter shape used by the executable Query options helper. */
export type PaginatedPageQuery<Row, PageParam, Meta extends object = Record<never, never>> = (context: {
  pageParam: PageParam;
  signal: AbortSignal;
}) => Promise<PaginatedPage<Row, PageParam, Meta>>;

/**
 * Low-level options for the pure Query helper. Production surfaces do not
 * construct this directly: usePaginatedResource supplies the key and adapter
 * from a PaginatedRouteResource after resolving the authenticated scope.
 */
export type PaginatedResourceOptions<
  Row,
  PageParam,
  Meta extends object = Record<never, never>,
  Key extends QueryKey = QueryKey,
> = {
  queryKey: Key;
  initialPageParam: PageParam;
  /** Omit only for the unresolved, disabled scope parked under the inert key. */
  queryPage?: PaginatedPageQuery<Row, PageParam, Meta>;
  enabled?: boolean;
};

export type PaginatedResourceQueryOptions<
  Row,
  PageParam,
  Meta extends object = Record<never, never>,
  Key extends QueryKey = QueryKey,
> = UseInfiniteQueryOptions<
  PaginatedPage<Row, PageParam, Meta>,
  Error,
  InfiniteData<PaginatedPage<Row, PageParam, Meta>, PageParam>,
  Key,
  PageParam
>;

/**
 * Build the narrow options object that goes to useInfiniteQuery. This remains
 * renderer-free so signal forwarding, page params, and retry behavior can be
 * proved with an InfiniteQueryObserver.
 */
export function paginatedResourceQueryOptions<
  Row,
  PageParam,
  Meta extends object = Record<never, never>,
  Key extends QueryKey = QueryKey,
>(
  options: PaginatedResourceOptions<Row, PageParam, Meta, Key>,
): PaginatedResourceQueryOptions<Row, PageParam, Meta, Key> {
  if (!options.queryPage && options.enabled !== false) {
    throw new Error('paginatedResourceQueryOptions: queryPage may be absent only while the resource is disabled');
  }
  return {
    queryKey: options.queryKey,
    // Infinite Query, like the one-shot runtime, must have no runnable
    // function under an unresolved identity. `enabled: false` alone does not
    // protect against an explicit refetch; skipToken makes that boundary real.
    queryFn: options.queryPage
      ? ({pageParam, signal}) => options.queryPage!({pageParam: pageParam as PageParam, signal})
      : skipToken,
    initialPageParam: options.initialPageParam,
    getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
    enabled: options.enabled ?? true,
  };
}

export function flattenPaginatedRows<Row, PageParam, Meta extends object = Record<never, never>>(
  data: InfiniteData<PaginatedPage<Row, PageParam, Meta>, PageParam> | undefined,
): Row[] {
  return data?.pages.flatMap((page) => page.rows) ?? [];
}

export type UsePaginatedResourceOptions = {
  enabled?: boolean;
  client?: SupabaseClient;
};

/** The small, renderer-free subset the result projection needs from Query. */
export type PaginatedResourceQueryLike<Row, PageParam, Meta extends object = Record<never, never>> = {
  data: InfiniteData<PaginatedPage<Row, PageParam, Meta>, PageParam> | undefined;
  isPending: boolean;
  isFetchingNextPage: boolean;
  isError: boolean;
  isFetchNextPageError: boolean;
  isRefetchError: boolean;
  error: Error | null;
  hasNextPage: boolean | undefined;
};

/**
 * What an infinite result makes safe for a surface to render. `firstPage`
 * preserves resource-owned metadata such as an exact server count; a surface
 * must never recalculate that from the incrementally fetched `rows` array.
 */
export type PaginatedResourceProjection<Row, PageParam, Meta extends object = Record<never, never>> = {
  rows: Row[];
  pages: readonly PaginatedPage<Row, PageParam, Meta>[];
  firstPage: PaginatedPage<Row, PageParam, Meta> | null;
  loading: boolean;
  loadingMore: boolean;
  /** A first-page failure. No empty state may be inferred from it. */
  error: Error | null;
  /** A failed later page; previously usable pages stay in rows. */
  loadMoreError: Error | null;
  /** A same-key refresh failure; previously usable pages stay in rows. */
  refreshError: Error | null;
  hasMore: boolean;
};

export type PaginatedResourceResult<
  Row,
  PageParam,
  Meta extends object = Record<never, never>,
> = PaginatedResourceProjection<Row, PageParam, Meta> & {
  /** The exact resource id whose scoped pages produced these rows. */
  resourceId: string;
  loadMore: () => Promise<unknown>;
  retryLoadMore: () => Promise<unknown>;
  refetch: () => Promise<unknown>;
};

/**
 * Project a Query result without rendering it. Keeping count/metadata and
 * refresh state here lets the contract be driven by an InfiniteQueryObserver
 * in this repository's renderer-less test environment.
 */
export function paginatedResourceResult<Row, PageParam, Meta extends object = Record<never, never>>(
  query: PaginatedResourceQueryLike<Row, PageParam, Meta>,
  ready: boolean,
): PaginatedResourceProjection<Row, PageParam, Meta> {
  // A disabled or unresolved resource is deliberately blank, even if Query
  // still has cache data for a formerly ready key. This mirrors the one-shot
  // runtime and prevents an old identity from becoming visible for one paint.
  const fetchedPages: readonly PaginatedPage<Row, PageParam, Meta>[] = query.data?.pages ?? [];
  const pages = ready ? fetchedPages : [];
  const firstPage = pages[0] ?? null;

  return {
    rows: ready ? flattenPaginatedRows(query.data) : [],
    pages,
    firstPage,
    loading: !ready || (query.isPending && !query.data),
    loadingMore: ready && query.isFetchingNextPage,
    error: ready && query.isError && !query.data ? query.error : null,
    loadMoreError: ready && query.isFetchNextPageError ? query.error : null,
    refreshError: ready && query.isRefetchError && !query.isFetchNextPageError ? query.error : null,
    hasMore: ready && Boolean(query.hasNextPage),
  };
}

/**
 * Resolve a paginated catalog resource under the full authenticated QueryScope.
 *
 * A filter/search/reload change belongs in the resource's pageKey parameters.
 * That produces a new Query identity, so TanStack cancels the superseded
 * observer when the adapter reads context.signal and cannot append a response
 * from an old generation into the current rows. The cursor never enters that
 * key: it remains pageParam state, sourced from each page's raw server result.
 */
export function usePaginatedResource<Row, PageParam, Meta extends object = Record<never, never>>(
  resourceId: string,
  params?: ResourceParams,
  {enabled = true, client = sb}: UsePaginatedResourceOptions = {},
): PaginatedResourceResult<Row, PageParam, Meta> {
  const {authState, rolePreview} = useAuth();
  // Runtime ids are strings, so this is the same explicit boundary as
  // useRouteResource's unknown one-shot payload. The kind accessor is the
  // important guard: a one-shot entry can never reach useInfiniteQuery.
  const resource = getPaginatedResource(resourceId) as unknown as PaginatedRouteResource<Row, PageParam, Meta>;
  const scope = scopeOrNull(authState, rolePreview);
  const ready = Boolean(scope && enabled);

  const options = useMemo(
    () =>
      paginatedResourceQueryOptions<Row, PageParam, Meta>({
        // Never manufacture a scope. A parked observer shares only the reviewed
        // inert key and remains disabled with skipToken, so even an explicit
        // refetch cannot issue a transport request before identity settles.
        queryKey: scope ? resource.pageKey(scope, params) : UNRESOLVED_RESOURCE_KEY,
        initialPageParam: resource.initialPageParam,
        queryPage: scope ? ({pageParam, signal}) => resource.queryPage(client, params, pageParam, signal) : undefined,
        enabled: ready,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scope and params are compared by value through their serializations
    [resource, scope && JSON.stringify(scope), params && JSON.stringify(params), ready, client],
  );

  const query = useInfiniteQuery(options);
  const projection = paginatedResourceResult(query, ready);
  const {fetchNextPage, isFetchingNextPage} = query;

  // Query allows refetch() on a disabled observer. The one-shot runtime's
  // no-op-before-scope contract applies here too, so an unresolved identity
  // cannot turn the inert key into a transport request through an event handler.
  const readyRef = useRef(ready);
  readyRef.current = ready;
  const refetchRef = useRef<(() => Promise<unknown>) | null>(null);
  refetchRef.current = query.refetch;

  const loadMore = useCallback(
    () => (ready && !isFetchingNextPage && query.hasNextPage ? fetchNextPage() : Promise.resolve()),
    [fetchNextPage, isFetchingNextPage, query.hasNextPage, ready],
  );
  const retryLoadMore = useCallback(
    () => (ready && !isFetchingNextPage && query.hasNextPage ? fetchNextPage() : Promise.resolve()),
    [fetchNextPage, isFetchingNextPage, query.hasNextPage, ready],
  );
  const refetch = useCallback(() => (readyRef.current ? refetchRef.current!() : Promise.resolve()), []);

  return {
    resourceId: resource.id,
    ...projection,
    loadMore,
    retryLoadMore,
    refetch,
  };
}
